// TrackedItemRow primitive: escalation confirmation modal.
//
// Universal across every consumer. Requires a non-empty EscalationReason
// (min 5 chars) which the consumer's escalate endpoint persists on a new
// EscalationRecord via the existing IEscalationService.

import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/design-system/components/ui";
import { Textarea } from "@/components/ui/Select";

const MIN_REASON_LENGTH = 5;

interface TrackedItemEscalateModalProps {
  open: boolean;
  onClose: () => void;
  itemTitle: string;
  escalationPath?: string;
  onConfirm: (reason: string) => void | Promise<void>;
  pending?: boolean;
}

export function TrackedItemEscalateModal({
  open,
  onClose,
  itemTitle,
  escalationPath,
  onConfirm,
  pending = false,
}: TrackedItemEscalateModalProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (reason.trim().length < MIN_REASON_LENGTH) {
      setError(`Please describe why you're escalating (at least ${MIN_REASON_LENGTH} characters).`);
      return;
    }
    setError(null);
    await onConfirm(reason.trim());
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Escalate item"
      description={`Routes "${itemTitle}" to the configured executive for resolution.`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void handleConfirm()} disabled={pending}>
            {pending ? "Escalating…" : "Escalate"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <p className="text-small text-onyx m-0">
          <span className="font-display text-label uppercase tracking-label text-gold-shadow">
            Escalation path
          </span>
          <br />
          {escalationPath ?? "Escalation routes to the configured executive lead."}
        </p>

        <div>
          <label className="font-display text-label uppercase tracking-label text-gold-shadow">
            Why are you escalating?
          </label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={5}
            invalid={!!error}
            className="mt-space-1"
            placeholder="Describe the situation and what decision you're asking for."
          />
          {error && (
            <div role="alert" className="text-status-error text-small mt-space-1">
              {error}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
